import { ApiMethod } from 'services/api';
import { useRestMutation } from 'utils/hooks/api/useRestMutation';
import { selectUserProjectTimeZone } from 'core/userContext';
import { selectProjectId } from 'modules/campaignAnalytics';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useDispatch, useSelector } from 'react-redux';
import { UPSERT_CAMPAIGN_API_URL } from 'modules/campaigns/lib/hooks';
import { useCampaignContext, useEditorContext } from '../../context';
import { getSaveCampaignPayload } from './utils';
import { CampaignEditorFormState, CampaignStep } from '../../types';
import {
  getUpdateCampaignMutationPayload,
  useUpdateCampaignMutation,
} from '../useUpdateCampaignMutation';

export const useSaveCampaign = () => {
  const dispatch = useDispatch();
  const { currentEditableStep, completeCampaignStep } = useEditorContext();

  const { mutate, isLoading } = useRestMutation(UPSERT_CAMPAIGN_API_URL, ApiMethod.post);
  const projectId = useSelector(selectProjectId);
  const projectTimeZone = useSelector(selectUserProjectTimeZone);
  const { campaign } = useCampaignContext();

  const { updateCampaign } = useUpdateCampaignMutation();

  const saveCampaign = (formValues: CampaignEditorFormState, onSuccess: () => void) => {
    const variables = getUpdateCampaignMutationPayload(campaign.id, formValues);
    const payload = getSaveCampaignPayload(campaign, formValues, {
      projectId,
      projectTimeZone,
      currentEditableStep,
    });

    // graphql mutation
    // updates parts of the form that are not updated via campaigns/createSubmit
    if (currentEditableStep === CampaignStep.Delivery) {
      updateCampaign({ variables });
    }

    return mutate(payload, {
      onError: (error) => {
        const errorMessage = `Error saving ${campaign.name}. Please try again later.`;
        dispatch(addNegativeToastWithErrorId(errorMessage, 'saveCampaign', error));
      },
      onSuccess: () => {
        dispatch(addPositiveToast(`${campaign.name} was successfully updated.`));

        if (currentEditableStep) {
          completeCampaignStep(currentEditableStep, false);
        }

        onSuccess();
      },
    });
  };

  return { saveCampaign, isLoading };
};
